
import { cloneDeep } from 'lodash';
import { Options, Vue } from 'vue-class-component';
import { Watch } from 'vue-property-decorator';
import { differenceOf } from '@/definitions/common/utils';
import { EmptySettings } from '@/api/models/Settings';
import { aclModule } from '@/store/acl';
import { ObjectsSingleToMulti } from '@/store/application/data.assets';
import { dataAssetsModule } from '@/store/application/data.assets.module';
import { configModule } from '@/store/config';
import { settingsItemModule } from '@/store/data/SettingsItemModule';
import { NInputNumber, NSwitch } from '@/uikit';
import NButton from '@/uikit/buttons/NButton.vue';
import NConfidence from '@/uikit/confidence/NConfidence.vue';
import NForm, { IFormContext, IFormLayout, IFormModel } from '@/uikit/forms/NForm.vue';
import { getNumberValidator } from '@/uikit/forms/validators';
import NTabs from '@/uikit/tabs/NTabs.vue';
import NTextDelimiter from '@/uikit/text/NTextDelimiter.vue';
import EntityBlank from '@/components/common/EntityBlank.vue';
import VMSCleanupForm from '@/components/settings/VMSCleanupForm.vue';
import SettingsPageLayout from '@/pages/settings/SettingsPageLayout.vue';
import { computeHoursInSeconds, computeSecondsInHours } from './utils';
import { dataModule } from '@/store/data';

const DEFAULT_CLEANUP_THRESHOLD_IN_SECONDS = 3600;

const VMSSettings = 'vms-settings';

@Options({
  name: 'SettingsPage',
  components: { EntityBlank, NButton, NForm, NTabs, SettingsPageLayout, VMSCleanupForm }
})
export default class SettingsPage extends Vue {
  tabName = 'face';
  newModel: IFormModel = {};
  get options() {
    return {
      VMSSettings
    };
  }

  get dataQa() {
    return this.$route.name;
  }

  get oldModel() {
    return settingsItemModule.item ?? EmptySettings;
  }

  get tabs() {
    const result = dataAssetsModule.availableObjects.map((objectName) => {
      return { name: objectName, label: this.$t(`common.${ObjectsSingleToMulti[objectName]}`, 'f') };
    });

    if (configModule.features.vms_enabled) {
      result.push({ name: VMSSettings, label: this.$t('settings.vms_cleanup_settings') });
    }
    return result;
  }

  get changes() {
    let result = differenceOf(this.newModel, this.oldModel);
    result = result['vms_cleanup_settings'] ? { ...result, vms_cleanup_settings: this.newModel['vms_cleanup_settings'] } : result;
    result = result['extended_event_analysis'] ? { ...result, extended_event_analysis: this.newModel['extended_event_analysis'] } : result;
    return result;
  }

  get hasChanges(): boolean {
    return Object.keys(this.changes).length > 0;
  }

  get hasViewAcl(): boolean {
    return true;
  }

  get hasUpdateAcl(): boolean {
    return aclModule.getAccess('ffsecurity.change_runtimesetting');
  }

  get currentLookupDepth() {
    return this.newModel.extended_event_analysis?.lookup_depth || 1;
  }

  get formLayout(): IFormLayout {
    if (this.tabName === VMSSettings) return [];
    const objectName = this.tabName;

    const confidenceDelimiter = {
      classes: 'label-m n-form-w-6 n-form-pad-10',
      component: NTextDelimiter,
      props: { i18n_label: 'common.confidence' }
    };

    const threshold = {
      path: `${objectName}_confidence_threshold`,
      classes: 'label-m n-form-w-2 n-form-label-horizontal-200 n-form-pad-10',
      i18n_label: 'settings.generic_confidence_threshold',
      component: NConfidence,
      i18n_tooltip: `ds.params.confidence_${objectName}_desc`,
      tooltipPlacement: 'bottom',
      props: { controls: true }
    };

    const jpegQualityDelimiter = {
      classes: 'label-m n-form-w-6 n-form-pad-10',
      component: NTextDelimiter,
      props: { i18n_label: 'settings.jpeg_quality' }
    };

    const jpegQuality = {
      path: 'thumbnail_jpeg_quality',
      i18n_label: 'settings.thumbnail_jpeg_quality',
      classes: 'label-m n-form-w-2 n-form-label-horizontal-200 n-form-control-120 n-form-pad-10',
      component: NInputNumber,
      props: { units: '%', min: 50, max: 95 },
      validators: [getNumberValidator({ required: false, min: 50, max: 95 })]
    };

    const localStrangerFields = [
      {
        classes: 'label-m n-form-w-6 n-form-pad-10',
        component: NTextDelimiter,
        props: { i18n_label: 'settings.local_stranger' }
      },
      {
        path: 'extended_event_analysis.lookup_depth',
        i18n_label: 'settings.local_stranger_lookup_depth',
        classes: 'label-m n-form-w-2 n-form-label-horizontal-350 n-form-control-120 n-form-pad-20',
        component: NInputNumber,
        props: { i18n_units: 'settings.local_stranger_days', min: 5, max: 10 },
        validators: [getNumberValidator({ required: false, min: 5, max: 10 })],
      },
      {
        path: 'extended_event_analysis.days_to_become_local',
        i18n_label: 'settings.local_stranger_days_to_become_local',
        classes: 'label-m n-form-w-2 n-form-label-horizontal-350 n-form-control-120 n-form-pad-20',
        component: NInputNumber,
        props: { i18n_units: 'settings.local_stranger_days', min: 1, max: this.currentLookupDepth },
        validators: [getNumberValidator({ required: false, min: 1, max: this.currentLookupDepth })],
      },
    ];

    const eventsCleanupFields = [
      {
        classes: 'label-m n-form-w-6 n-form-pad-10',
        component: NTextDelimiter,
        props: { i18n_label: 'settings.events_cleanup' }
      },
      {
        path: `${objectName}_events_max_matched_age`,
        i18n_label: 'settings.delete_matched_events_older_than',
        i18n_tooltip: 'settings.delete_events_cleanup_fields_desc',
        classes: 'label-m n-form-w-2 n-form-label-horizontal-350 n-form-control-120 n-form-pad-20',
        component: NInputNumber,
        props: { i18n_units: 'settings.hours', min: 0, max: 239976 },
        validators: [getNumberValidator({ required: false, min: 0 })],
        decode: function (this: IFormContext) {
          const valueInSeconds = this.model[`${objectName}_events_max_matched_age`] || DEFAULT_CLEANUP_THRESHOLD_IN_SECONDS;
          return computeHoursInSeconds(valueInSeconds);
        },
        encode: function (this: IFormContext, _model: IFormModel, value: number) {
          this.model[`${objectName}_events_max_matched_age`] = computeSecondsInHours(value);
        }
      },
      {
        path: `${objectName}_events_max_unmatched_age`,
        i18n_label: 'settings.delete_unmatched_events_older_than',
        i18n_tooltip: 'settings.delete_events_cleanup_fields_desc',
        classes: 'label-m n-form-w-2 n-form-label-horizontal-350 n-form-control-120 n-form-pad-10',
        component: NInputNumber,
        props: { i18n_units: 'settings.hours', min: 0, max: 239976 },
        validators: [getNumberValidator({ required: false, min: 0 })],
        decode: function (this: IFormContext) {
          const valueInSeconds = this.model[`${objectName}_events_max_unmatched_age`] || DEFAULT_CLEANUP_THRESHOLD_IN_SECONDS;
          return computeHoursInSeconds(valueInSeconds);
        },
        encode: function (this: IFormContext, _model: IFormModel, value: number) {
          this.model[`${objectName}_events_max_unmatched_age`] = computeSecondsInHours(value);
        }
      },
      {
        path: `${objectName}_events_max_fullframe_matched_age`,
        i18n_label: 'settings.delete_full_frames_of_matched_events_older_than',
        i18n_tooltip: 'settings.delete_events_cleanup_fields_desc',
        classes: 'label-m n-form-w-2 n-form-label-horizontal-350 n-form-control-120 n-form-pad-10',
        component: NInputNumber,
        props: { i18n_units: 'settings.hours', min: 0, max: 239976 },
        validators: [getNumberValidator({ required: false, min: 0 })],
        decode: function (this: IFormContext) {
          const valueInSeconds = this.model[`${objectName}_events_max_fullframe_matched_age`] || DEFAULT_CLEANUP_THRESHOLD_IN_SECONDS;
          return computeHoursInSeconds(valueInSeconds);
        },
        encode: function (this: IFormContext, _model: IFormModel, value: number) {
          this.model[`${objectName}_events_max_fullframe_matched_age`] = computeSecondsInHours(value);
        }
      },
      {
        path: `${objectName}_events_max_fullframe_unmatched_age`,
        i18n_label: 'settings.delete_full_frames_of_unmatched_events_older_than',
        i18n_tooltip: 'settings.delete_events_cleanup_fields_desc',
        classes: 'label-m n-form-w-2 n-form-label-horizontal-350 n-form-control-120 n-form-pad-10',
        component: NInputNumber,
        props: { i18n_units: 'settings.hours', min: 0, max: 239976 },
        validators: [getNumberValidator({ required: false, min: 0 })],
        decode: function (this: IFormContext) {
          const valueInSeconds = this.model[`${objectName}_events_max_fullframe_unmatched_age`] || DEFAULT_CLEANUP_THRESHOLD_IN_SECONDS;
          return computeHoursInSeconds(valueInSeconds);
        },
        encode: function (this: IFormContext, _model: IFormModel, value: number) {
          this.model[`${objectName}_events_max_fullframe_unmatched_age`] = computeSecondsInHours(value);
        }
      }
    ];

    const hasEvents = configModule.features.events_enabled;
    const hasExtendedEventAnalysis = configModule.features.extended_event_analysis_enabled;
    const layout: IFormLayout = [
      confidenceDelimiter,
      threshold,
      jpegQualityDelimiter,
      jpegQuality,
    ];
    hasExtendedEventAnalysis && dataModule.isCurrentUserAdmin && objectName === 'face' && layout.push(...localStrangerFields);
    hasEvents && layout.push(...eventsCleanupFields);

    return layout;
  }

  @Watch('newModel.extended_event_analysis.lookup_depth')
  setDaysToBecomeLocalMaxValue(v: number) {
    if (this.newModel.extended_event_analysis?.days_to_become_local > this.newModel.extended_event_analysis?.lookup_depth) {
      this.newModel.extended_event_analysis.days_to_become_local = v;
    }
  }

  async loadSettings() {
    await settingsItemModule.get();
    this.newModel = cloneDeep(this.oldModel);
  }

  async saveSettings() {
    const valid = this.$refs.form?.validate() ?? true;

    if (!valid) {
      this.$refs.form?.displayErrors();
      return;
    }
    try {
      await settingsItemModule.saveFields(this.changes);
      await this.loadSettings();
    } catch (e) {
      console.error(e);
    }
  }

  async mounted() {
    await this.loadSettings();
  }

  async update() {
    await this.saveSettings();
  }
}
